/**
 * Centralized constants for survey-related configurations
 */
import type { SvgIconComponent } from '@mui/icons-material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TitleIcon from '@mui/icons-material/Title';

/**
 * Preset label pairs for True/False questions
 */
export const TRUE_FALSE_PRESETS = [
  { trueLabel: 'True', falseLabel: 'False' },
  { trueLabel: 'Yes', falseLabel: 'No' },
  { trueLabel: 'Agree', falseLabel: 'Disagree' },
  { trueLabel: 'Correct', falseLabel: 'Incorrect' },
] as const;

/**
 * Survey status values
 */
export const SURVEY_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
} as const;

/**
 * Question type codes matching backend QuestionTypeCode enum
 */
export const QUESTION_TYPES = {
  SECTION: 'SECTION',
  OPEN_TEXT: 'OPEN_TEXT',
  SINGLE_CHOICE: 'SINGLE_CHOICE',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  RANKED_CHOICE: 'RANKED_CHOICE',
  SCALE_5: 'SCALE5',
  TRUE_FALSE: 'TRUE_FALSE',
} as const;

/**
 * Question type category for grouping in UI
 */
export type QuestionTypeCategory = 'structure' | 'text' | 'choice' | 'scale';

/**
 * Configuration for each question type including icon, color, and category
 */
export interface QuestionTypeConfig {
  icon: SvgIconComponent;
  color: string;
  category: QuestionTypeCategory;
}

/**
 * Centralized question type visual configuration
 * Used by QuestionTypeMenu, FloatingToolbar, InlineQuestionEditor, etc.
 */
export const QUESTION_TYPE_CONFIG: Record<string, QuestionTypeConfig> = {
  SECTION: { icon: TitleIcon, color: '#14B8A6', category: 'structure' },
  OPEN_TEXT: { icon: TextFieldsIcon, color: '#0891B2', category: 'text' },
  SINGLE_CHOICE: { icon: RadioButtonCheckedIcon, color: '#0ea5e9', category: 'choice' },
  MULTIPLE_CHOICE: { icon: CheckBoxIcon, color: '#10b981', category: 'choice' },
  RANKED_CHOICE: { icon: FormatListNumberedIcon, color: '#0F766E', category: 'scale' },
  SCALE5: { icon: LinearScaleIcon, color: '#f59e0b', category: 'scale' },
  TRUE_FALSE: { icon: ToggleOnIcon, color: '#ec4899', category: 'scale' },
} as const;

/**
 * Category display order for question type menus
 */
export const QUESTION_TYPE_CATEGORY_ORDER: QuestionTypeCategory[] = ['structure', 'text', 'choice', 'scale'];

/**
 * Default labels for Scale 5 (Likert scale) questions
 */
export const SCALE_5_LABELS = [
  'Strongly Disagree',
  'Disagree',
  'Neutral',
  'Agree',
  'Strongly Agree',
] as const;

/**
 * Special option ID for "Other" selections in ranked choice
 */
export const OTHER_OPTION_ID = -999;

/**
 * Brand color for consistent theming
 */
export const BRAND_COLOR = '#14B8A6';

/**
 * Type exports for the constants
 */
export type SurveyStatus = typeof SURVEY_STATUS[keyof typeof SURVEY_STATUS];
export type QuestionTypeCode = typeof QUESTION_TYPES[keyof typeof QUESTION_TYPES];
export type TrueFalsePreset = typeof TRUE_FALSE_PRESETS[number];
